import { useEffect, useState } from 'react';
import { MoreVertical } from 'lucide-react';
import { Button, Card, Alert, Input, Select, ThreeDotPopover } from '@dreamtree-org/twreact-ui';
import { useAppApi } from '@/lib/api';
import { groupConnectionOptions } from '@/lib/connectionOptions';
import Can from '@rbac/Can';
import { visibleFields, staleFieldNames } from '@/lib/fieldVisibility';

/**
 * The parts of provider management that are identical whether the provider is an HTTP API or
 * an MCP server, shared by ToolProviderDetail and McpServerDetail.
 *
 * They live here rather than being copied because each encodes a server-side rule that the UI
 * must not contradict — the approve-before-enable ordering, the auto/destructive
 * contradiction, the credential precedence. A copy would let one screen keep enforcing a rule
 * after the other stopped.
 */

export const CATEGORY_HELP = {
  auto: 'Runs with no human in the loop. Only for reads.',
  confirm: 'The end user allows or denies it inline in the widget, with the arguments shown.',
  gated: 'The Human Proxy decides inline. Denied outright on unattended surfaces.',
};
export const CATEGORY_OPTIONS = Object.keys(CATEGORY_HELP).map((value) => ({ value, label: value }));

/** `ask` is not a choice an admin makes: services/tools/mcpProvider.js binds every
 *  `ask_user` as `ask` whatever the stored category says, so the row shows that instead of
 *  a select whose value would be a lie. */
export const ASK_HELP = 'Pauses the conversation and asks the end user — never runs unattended.';
export function isAskTool(tool) {
  return !!tool && (tool.name === 'ask_user' || tool.category === 'ask');
}

/** MCP `_meta.kind` values (and non-GET HTTP tools) that describe a write. The server does not stop an admin
 *  choosing `auto` for these (it is their call), but the help text says "only for reads",
 *  so the row must not stay silent when that is exactly what is configured. */
const WRITE_KINDS = new Set(['write', 'delete', 'destructive', 'create', 'update', 'mutate', 'action']);
export function isAutoWrite(tool) {
  if (!tool || tool.category !== 'auto' || isAskTool(tool)) return false;
  if (tool.http_method && !['GET', 'HEAD'].includes(String(tool.http_method).toUpperCase())) return true;
  return WRITE_KINDS.has(String(tool.meta_kind || '').toLowerCase());
}

export function AutoWriteNote() {
  return (
    <p className="text-[11px] text-amber-600 mt-1 max-w-xs">
      Write tool on <code>auto</code> — it changes data with no human check. Prefer{' '}
      <code>confirm</code> or <code>gated</code>.
    </p>
  );
}

/**
 * Per-tool writes, with the busy tool tracked by id so a table of 40 rows disables only the
 * row being written rather than all of them.
 */
export function useToolActions(providerId, { onChanged, onError }) {
  const { fetchApi } = useAppApi();
  const [busyId, setBusyId] = useState(null);

  const patch = async (tool, data) => {
    setBusyId(tool.id);
    try {
      await fetchApi('providerToolUpdate', { providerId, toolId: tool.id, ...data });
      onChanged();
    } catch (ex) {
      onError(ex?.response?.data?.error || ex.message);
    } finally {
      setBusyId(null);
    }
  };

  /** A category change is a full redeclaration server-side (it re-validates the name and
   *  schema), so the unchanged fields have to ride along.
   *
   *  `destructive` is cleared when the human picks `auto`, because the server refuses a tool
   *  that is both — and every MCP tool from a server that sends no `readOnlyHint` is stored
   *  destructive by default, so without this, choosing `auto` on a read-only synced tool
   *  could only ever 400. */
  const changeCategory = (tool, category) => {
    // A cleared select reports null/''; the server would 400 on it, so ignore it here.
    if (!category || category === tool.category) return Promise.resolve();
    let inputSchema = {};
    try { inputSchema = JSON.parse(tool.input_schema || '{}'); } catch { inputSchema = {}; }
    return patch(tool, {
      category,
      name: tool.name,
      description: tool.description,
      inputSchema,
      httpMethod: tool.http_method,
      httpPath: tool.http_path,
      destructive: category === 'auto' ? false : !!tool.destructive,
      confirmPrompt: tool.confirm_prompt,
    });
  };

  return { busyId, patch, changeCategory };
}

/** Why a synced MCP tool is destructive by default, shown where the choice is made. */
export function DestructiveNote() {
  return (
    <p className="text-[11px] text-secondary-500 mt-1 max-w-xs">
      Marked destructive — an MCP server that sends no <code>readOnlyHint</code> is classified
      this way by default, and destructive tools always keep the human gate. Switching a
      genuinely read-only tool to <code>auto</code> clears it.
    </p>
  );
}

/** One decision about one remote server, instead of two switch flips per row. Same gate as
 *  the per-row switches — the server applies assertApproveBeforeEnable either way.
 *
 *  `selectedIds` is the checkbox selection (empty = act on `visibleIds`, i.e. every tool
 *  currently matching the table's filters/search — the select-all header resolves to ids
 *  client-side, the server never sees search state). The write always sends explicit
 *  `toolIds` so a filtered view can never arm tools the user is not looking at. */
export function BulkArmBar({
  tools, providerId, selectedIds = [], visibleIds = [], onChanged, onError, onClearSelection,
}) {
  const { fetchApi } = useAppApi();
  const [busy, setBusy] = useState(false);
  const armed = tools.filter((t) => t.approved && t.enabled).length;
  const hasSelection = selectedIds.length > 0;
  // Fall back to the visible scope when nothing is ticked, so the menu keeps the old
  // "approve everything" behaviour without a separate set of buttons.
  const scopeIds = hasSelection ? selectedIds : visibleIds;
  const scopeLabel = hasSelection ? `${selectedIds.length} selected` : `all ${visibleIds.length} matching`;
  const usable = !busy && scopeIds.length > 0;

  const bulk = async (data) => {
    if (!scopeIds.length) return;
    setBusy(true);
    try {
      await fetchApi('providerToolsBulk', { providerId, toolIds: scopeIds, ...data });
      if (onClearSelection) onClearSelection();
      onChanged();
    } catch (ex) {
      onError(ex?.response?.data?.error || ex.message);
    } finally {
      setBusy(false);
    }
  };

  const items = [
    { key: 'scope', label: scopeIds.length ? `Applies to ${scopeLabel}` : 'Nothing matches — nothing to do', disabled: true },
    { key: 'arm', label: 'Approve & enable', disabled: !usable, onClick: () => bulk({ approved: true, enabled: true }) },
    { key: 'approve', label: 'Approve', disabled: !usable, onClick: () => bulk({ approved: true }) },
    { key: 'disable', label: 'Disable', disabled: !usable, onClick: () => bulk({ enabled: false }) },
    { key: 'cat-auto', label: 'Set category: auto', disabled: !usable, onClick: () => bulk({ category: 'auto' }) },
    { key: 'cat-confirm', label: 'Set category: confirm', disabled: !usable, onClick: () => bulk({ category: 'confirm' }) },
    { key: 'cat-gated', label: 'Set category: gated', disabled: !usable, onClick: () => bulk({ category: 'gated' }) },
  ];
  if (hasSelection) {
    items.push({ key: 'clear', label: 'Clear selection', disabled: busy, onClick: onClearSelection });
  }

  // menuClass: the library menu has no z-index and a hard-coded white background, so it
  // slid under the table's search box and ignored dark mode.
  return (
    <div className="flex flex-wrap items-center justify-end gap-2">
      <span className="text-xs text-secondary-500 mr-1">
        {armed} of {tools.length} armed — only approved AND enabled tools are bound to an agent.
        {hasSelection ? ` ${selectedIds.length} selected.` : ''}
      </span>
      <ThreeDotPopover
        items={items}
        ariaLabel="Bulk tool actions"
        menuClass="right-0 left-auto z-50 min-w-[220px] bg-[var(--app-surface)] border-[var(--app-border)] shadow-lg"
        trigger={(
          <button
            type="button"
            aria-label="Bulk tool actions"
            title="Bulk actions for the selected or matching tools"
            disabled={busy || !tools.length}
            className="inline-flex items-center gap-1 rounded-md border border-[var(--app-border)] px-2.5 py-1.5 text-sm hover:bg-[var(--app-border)]/20 disabled:opacity-50"
          >
            <MoreVertical size={14} />
            Bulk actions
          </button>
        )}
      />
    </div>
  );
}

/**
 * How WE authenticate TO the customer's endpoint — the opposite direction from the signing
 * secret below, and the reason a correctly-synced MCP server can still answer every call with
 * "not authenticated".
 *
 * services/tools/mcpProvider.js's authHeadersFor reads the bound Connection's decrypted
 * credentials and sends `Authorization: Bearer <bearer_token>`, or
 * `<api_key_header || x-api-key>: <api_key>`. With nothing bound it falls back to this
 * provider's own signing secret as a bearer, which is only right when the customer's server
 * was configured to accept that exact value.
 */
/**
 * One dynamic field of the inline credential form — same spec-driven shapes as
 * ConnectionDetail.jsx's FieldInput (string/password/number/boolean/textarea/select/url),
 * kept local so this shared panel doesn't import a page. Values stay strings except
 * booleans; the server coerces types and drops blank entries on save.
 */
function InlineCredentialField({ field, value, onChange }) {
  if (field.type === 'boolean') {
    return (
      <label className="flex items-center gap-2 text-sm pt-5">
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} />
        {field.label}
      </label>
    );
  }
  if (field.type === 'select') {
    return (
      <div>
        <label className="block text-sm mb-1">{field.label}</label>
        <select
          className="border rounded-md px-2 py-1.5 text-sm bg-[var(--app-surface)] w-full"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="" disabled>Select…</option>
          {(field.options || []).map((opt) => <option key={opt} value={opt}>{opt}</option>)}
        </select>
      </div>
    );
  }
  if (field.type === 'textarea') {
    return (
      <div className="col-span-2">
        <label className="block text-sm mb-1">{field.label}</label>
        <textarea
          className="w-full min-h-[80px] rounded-md border border-[var(--app-border)] bg-[var(--app-surface)] p-2 text-sm font-mono"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder || ''}
          autoComplete="off"
        />
      </div>
    );
  }
  // Empty password renders as plain text (same convention as ConnectionDetail):
  // nothing secret to unmask yet; masks once a value is typed.
  const masked = field.type === 'password' && !!value;
  let inputType = 'text';
  if (masked) inputType = 'password';
  else if (field.type === 'number') inputType = 'number';
  return (
    <Input
      label={field.label}
      type={inputType}
      autoComplete="off"
      value={value ?? ''}
      placeholder={field.placeholder || ''}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

/**
 * Blank inputs are omitted (not sent as empty strings): the server treats a missing
 * credentials object as "no secret", while an explicit {} would encrypt an empty blob
 * and wrongly report has_credentials.
 */
function dropBlanks(obj) {
  return Object.fromEntries(
    Object.entries(obj || {}).filter(([, v]) => v !== '' && v !== null && v !== undefined),
  );
}

/** One spec-driven field grid (config or credentials) — null when the spec has none.
 *  `scope` (config ∪ credentials) resolves each field's showIf, so the same auth-type
 *  gating ConnectionDetail applies works here too; without it this inline form would
 *  collect secrets the server drops on write. */
function SpecFieldGrid({ fields, values, onField, scope }) {
  const shown = visibleFields(fields, scope);
  if (!shown.length) return null;
  return (
    <div className="grid grid-cols-2 gap-3">
      {shown.map((f) => (
        <InlineCredentialField key={f.name} field={f} value={values[f.name]} onChange={(v) => onField(f.name, v)} />
      ))}
    </div>
  );
}
/** First active provider of the `mcp` connection category — the generic row once seeded. */
function firstActiveMcpKey(spec) {
  return Object.values(spec.providers || {}).find((p) => p.category === 'mcp' && p.is_active !== false)?.key || '';
}

/**
 * Interactive OAuth for a remote MCP server — the "no client id or secret needed" flow: this
 * app self-registers with the server's authorization server (DCR), the admin approves in a
 * popup, and the resulting token lives on the tool provider.
 *
 * A popup rather than a full-page redirect because an XHR cannot follow a cross-origin
 * authorization redirect, and navigating the whole tab would lose the page's state. The
 * callback page posts back to us and closes; the poll is the fallback for browsers that
 * block the message.
 */
function connectLabel(busy, connected) {
  if (busy) return 'Waiting…';
  return connected ? 'Reconnect' : 'Connect…';
}

export function OAuthConnectPanel({ providerId, provider, onChanged, onError }) {
  const { fetchApi } = useAppApi();
  const [busy, setBusy] = useState(false);

  const expiresAt = provider.oauth_token_expires_at ? new Date(provider.oauth_token_expires_at) : null;
  const connected = !!provider.has_oauth && !!expiresAt;
  const expired = expiresAt ? expiresAt.getTime() < Date.now() : false;

  const connect = async () => {
    setBusy(true);
    try {
      const { authorizeUrl } = await fetchApi('toolProviderOauthStart', providerId);
      const popup = window.open(authorizeUrl, 'mcp-oauth', 'width=620,height=760');
      if (!popup) {
        onError('Your browser blocked the sign-in window — allow pop-ups for this site and try again.');
        return;
      }
      await waitForOAuth(popup);
      onChanged();
    } catch (ex) {
      onError(ex?.response?.data?.error || ex.message);
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      await fetchApi('toolProviderOauthDisconnect', providerId);
      onChanged();
    } catch (ex) {
      onError(ex?.response?.data?.error || ex.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-md border border-[var(--app-border)] p-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm">
          <span className="font-medium">Sign in with the server&apos;s own login</span>
          <p className="text-xs text-secondary-500">
            {connected && !expired && `Connected — token valid until ${expiresAt.toLocaleString()}.`}
            {connected && expired && 'Connected, but the token has expired — it renews automatically on the next call, or reconnect now.'}
            {!connected && 'No client id or secret needed: this app registers itself, then you approve in a pop-up.'}
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button size="sm" variant={connected ? 'secondary' : 'primary'} disabled={busy} onClick={connect}>
            {connectLabel(busy, connected)}
          </Button>
          {connected && <Button size="sm" variant="secondary" disabled={busy} onClick={disconnect}>Disconnect</Button>}
        </div>
      </div>
    </div>
  );
}

/**
 * Resolve when the OAuth popup reports back, or when it closes without reporting. Both paths
 * end the same way — the caller re-reads the provider — so a blocked postMessage degrades to
 * "refresh and see" rather than hanging.
 */
function waitForOAuth(popup) {
  return new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      window.removeEventListener('message', onMessage);
      clearInterval(timer);
      resolve();
    };
    const onMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data && event.data.source === 'mcp-oauth') finish();
    };
    window.addEventListener('message', onMessage);
    const timer = setInterval(() => { if (popup.closed) finish(); }, 500);
  });
}

/**
 * Inline "save an API key (or bearer/basic/OAuth material) without leaving this screen".
 * Creates a Connection record scoped to this tool provider's application, then binds it
 * via toolProviderUpdate — the same two calls as doing it on the Connections screen,
 * without the round-trip. Only providers of the `mcp` connection category are offered;
 * everything is optional server-side (public servers need no secret), but half-supplied
 * pairs (username without password, client id without secret) are rejected with a 400.
 */
function NewMcpConnectionForm({ provider, onDone, onError }) {
  const { fetchApi } = useAppApi();
  const [spec, setSpec] = useState(null);
  const [providerKey, setProviderKey] = useState('mcp');
  const [name, setName] = useState('');
  const [config, setConfig] = useState({});
  const [credentials, setCredentials] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchApi('connectionProviders')
      .then((res) => {
        setSpec(res);
        if (res && !res.providers?.mcp && Object.keys(res.providers || {}).length) {
          setProviderKey(firstActiveMcpKey(res));
        }
      })
      .catch((ex) => onError(ex?.response?.data?.error || ex.message));
  }, []);

  const mcpOptions = spec
    ? Object.values(spec.providers || {})
      .filter((p) => p.category === 'mcp' && p.is_active !== false)
      .map((p) => ({ value: p.key, label: p.label || p.key }))
    : [];
  const activeSpec = spec?.providers?.[providerKey] || null;
  const scope = { ...config, ...credentials };

  // Same rule as ConnectionDetail: changing a gating config field (auth_type) discards
  // whatever the previous branch collected, so a switched-away secret is never sent.
  const setConfigField = (key, value) => {
    const nextConfig = { ...config, [key]: value };
    setConfig(nextConfig);
    const stale = staleFieldNames(activeSpec?.credentialFields, { ...nextConfig, ...credentials });
    if (stale.length) setCredentials((c) => Object.fromEntries(Object.entries(c).filter(([k]) => !stale.includes(k))));
  };

  const save = async () => {
    if (!name.trim()) { onError('Give the credential a name first.'); return; }
    if (!activeSpec) { onError('Pick a connection provider first.'); return; }
    setSaving(true);
    try {
      const cleanConfig = dropBlanks(config);
      const cleanCreds = dropBlanks(credentials);
      const body = {
        providerId: activeSpec.id,
        name: name.trim(),
        application_id: provider.application_id ?? null,
      };
      if (Object.keys(cleanConfig).length) body.config = cleanConfig;
      if (Object.keys(cleanCreds).length) body.credentials = cleanCreds;
      const created = await fetchApi('connectionCreate', body);
      await fetchApi('toolProviderUpdate', { id: provider.id, authConnectionId: created.id });
      onDone();
    } catch (ex) {
      onError(ex?.response?.data?.error || ex.message);
    } finally {
      setSaving(false);
    }
  };

  if (!spec) return <p className="text-xs text-secondary-500">Loading credential types…</p>;
  if (!mcpOptions.length) {
    return (
      <Alert variant="warning">
        No MCP connection provider exists yet — run <code>npm run db:sync</code> (or
        <code>node commands/sync-connection-providers.js</code>) to seed the generic MCP
        provider, then retry. Meanwhile, create the credential on the Connections screen.
      </Alert>
    );
  }

  return (
    <div className="rounded-md border border-[var(--app-border)] p-3 space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm mb-1">Credential type</label>
          <select
            className="border rounded-md px-2 py-1.5 text-sm bg-[var(--app-surface)] w-full"
            value={providerKey}
            onChange={(e) => { setProviderKey(e.target.value); setConfig({}); setCredentials({}); }}
          >
            {mcpOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <Input
          label="Name"
          value={name}
          placeholder={`${provider.slug || 'mcp'} api key`}
          autoComplete="off"
          onChange={(e) => setName(e.target.value)}
        />
      </div>
      <SpecFieldGrid fields={activeSpec?.configFields} values={config} onField={setConfigField} scope={scope} />
      <SpecFieldGrid fields={activeSpec?.credentialFields} values={credentials} onField={(k, v) => setCredentials((c) => ({ ...c, [k]: v }))} scope={scope} />
      <p className="text-xs text-secondary-500">
        Saved in this provider&apos;s scope{provider.application_id != null ? '' : ' (company-wide, like this provider)'} and
        bound here immediately. Pick the auth type first — it decides which secrets are
        asked for. Choose <em>none</em> for a public server; the provider secret fallback
        and OAuth auto-discovery still apply.
      </p>
      <div className="flex gap-2">
        <Button size="sm" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save & bind'}</Button>
        <Button size="sm" variant="secondary" onClick={onDone} disabled={saving}>Cancel</Button>
      </div>
    </div>
  );
}

export function OutboundAuthPanel({ providerId, provider, onChanged, onError }) {
  const { fetchApi } = useAppApi();
  const [connections, setConnections] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showNew, setShowNew] = useState(false);

  const loadConnections = () => fetchApi('crud', { model: 'AppConnection', action: 'list', limit: 200 })
    .then((res) => setConnections((Array.isArray(res) ? res : res?.data) || []))
    .catch(() => setConnections([]));

  useEffect(() => {
    loadConnections();
  }, []);

  const save = async (value) => {
    setBusy(true);
    try {
      await fetchApi('toolProviderUpdate', { id: providerId, authConnectionId: value || null });
      onChanged();
    } catch (ex) {
      onError(ex?.response?.data?.error || ex.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-3 space-y-2">
      <h2 className="font-semibold">Authentication to this endpoint</h2>
      <Can module="toolproviders" action="author">
        <Select
          label="Credential used for outbound calls"
          // twreact's grouped mode flattens `g.options.forEach`, so EVERY top-level entry
          // must be a group — a bare {label,value} here blanks the page (see
          // client/src/lib/connectionOptions.js).
          options={[{ label: 'No credential', options: [{ value: '', label: "None — send this provider's own signing secret as a bearer" }] },
            ...groupConnectionOptions(connections)]}
          grouped
          searchable
          allowClear
          disabled={busy}
          value={provider.auth_connection_id || ''}
          onChange={save}
        />
        {!showNew
          ? <Button size="sm" variant="secondary" className="mt-2" onClick={() => setShowNew(true)}>New credential…</Button>
          : (
            <div className="mt-2">
              <Can
                module="connections"
                action="author"
                fallback={<p className="text-xs text-secondary-500">Creating credentials needs the connections.author permission — ask an admin, or create it on the Connections screen and pick it above.</p>}
              >
                <NewMcpConnectionForm
                  provider={provider}
                  onError={onError}
                  onDone={() => { setShowNew(false); loadConnections(); onChanged(); }}
                />
              </Can>
            </div>
          )}
      </Can>
      {provider.kind === 'mcp' && (
        <Can module="toolproviders" action="author">
          <OAuthConnectPanel providerId={providerId} provider={provider} onChanged={onChanged} onError={onError} />
        </Can>
      )}
      <p className="text-xs text-secondary-500">
        The connection&apos;s auth type decides the header: <em>bearer</em> becomes{' '}
        <code>Authorization: Bearer …</code>, <em>api key</em> becomes <code>x-api-key</code>{' '}
        (or the header named in <code>api_key_header</code>), <em>basic</em> becomes Basic. For a
        server that uses OAuth, leave the credential empty and use Connect above — the token is
        held here, not on the connection. The value is never shown to the model.
      </p>
    </Card>
  );
}

export function SecretPanel({ providerId, hasSecret, onError }) {
  const { fetchApi } = useAppApi();
  const [secret, setSecret] = useState(null);

  const rotate = async () => {
    try {
      const res = await fetchApi('toolProviderRotateSecret', providerId);
      setSecret(res.secret);
    } catch (ex) {
      onError(ex?.response?.data?.error || ex.message);
    }
  };

  return (
    <Card className="p-3 space-y-2">
      <h2 className="font-semibold">Signing secret</h2>
      <p className="text-sm text-secondary-500">
        Signs our calls into your API (<code>X-Signature</code>, <code>X-Agent-Actor</code>)
        and verifies the tool manifest your page presents. {hasSecret ? 'One is set.' : 'None is set.'}
      </p>
      {secret && (
        <>
          <Alert variant="warning" title="Shown only once — your integration must be redeployed with it">
            Rotating breaks the existing integration in both directions until the new value is deployed.
          </Alert>
          <pre className="bg-gray-900 text-gray-100 p-3 rounded text-xs break-all whitespace-pre-wrap">{secret}</pre>
        </>
      )}
      <Can module="toolproviders" action="rotate">
        <Button size="sm" variant="secondary" onClick={rotate}>Rotate secret</Button>
      </Can>
    </Card>
  );
}
